import { css } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { Resizable } from 're-resizable';
import { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useSDK } from '../../app';
import { useUIState } from '../../app/App';
import { styled } from '../../styled';
import { Dimensions } from '../../uiState/clientViewportStore';
import { useDrag } from '../../utils/useDrag';
import { useResize } from '../../utils/useResize';
import { useLoadingDots } from '../chat/connectionCorrupted/useLoadingDots';
import { ControlButtons } from './controlButtons';
import { msgs } from './msgs';
import { OperatorMuted } from './operatorMuted';
import { VideoPanelStyles } from './styles';
import { useFullscreen } from './useFullscreen';

const LoadingOperatorLabel = (): ReactElement => {
	const dotsAmount = useLoadingDots();
	const { formatMessage } = useIntl();

	return <span>{formatMessage(msgs.operatorWillBeConnectedSoon) + '.'.repeat(dotsAmount)}</span>;
};
type Pos = { right: number; bottom: number };
const useCenterPosition = (sizes: Dimensions): Pos => {
	const { clientViewport } = useUIState();

	return useMemo(() => {
		const { bottom, right } = {
			bottom: clientViewport.height / 2 - sizes.height / 2,
			right: clientViewport.width / 2 - sizes.width / 2,
		};
		return {
			bottom: bottom > 0 ? bottom : 0,
			right: right > 0 ? right : 0,
		};
	}, [clientViewport, sizes]);
};

const fullParentSize = { width: '100%', height: '100%' };

const VideoPannelWrapper = styled(
	({ className, children }: { className: string; children: ReactNode }): ReactElement => {
		const panelWrapper = useRef<HTMLDivElement>(null);
		const [sizes, setSizes] = useState({ width: 700, height: 700 / 1.33 });
		const [dragUsed, setDragUsed] = useState(false);
		const center = useCenterPosition(sizes);
		const { position, onMouseDown, setPosition, ...listeners } = useDrag(center, panelWrapper.current, center);

		const { clientViewport } = useUIState();

		const pos = clientViewport.width > 1000 ? position : {};
		const onResize = useResize(
			(pos: Partial<{ width: number; height: number }>) => setSizes((pr) => ({ ...pr, ...pos })),
			setPosition,
			position
		);

		if (clientViewport.width < 1000) {
			return <div>{children}</div>;
		}

		return (
			<div
				className={sizes.height > 325 && sizes.width > 433 ? className : className + ' local-video-hidden'}
				ref={panelWrapper}
				style={pos}
			>
				<Resizable
					size={sizes}
					minHeight={243.5}
					maxHeight={526}
					maxWidth={700}
					minWidth={325}
					bounds="window"
					onResize={onResize}
				>
					<div
						style={fullParentSize}
						{...listeners}
						onMouseDown={(e): void => {
							if (!dragUsed) {
								setDragUsed(true);
							}
							onMouseDown(e);
						}}
					>
						{children}
					</div>
				</Resizable>
			</div>
		);
	}
)(css`
	@media (min-width: 1000px) {
		position: fixed;
	}
`);

let timeout = 0;

export const VideoPanel = styled(
	observer(({ className }: { className: string }) => {
		const { videoCallsStore } = useSDK();
		const localVideo = useRef<HTMLVideoElement>(null);
		const remoteVideo = useRef<HTMLVideoElement>(null);
		const { panel, fullscreen, setFullscreen } = useFullscreen();
		const [showLocalVideo, setShowLocalVideo] = useState(false);

		useEffect(() => {
			const cb = (): void => {
				if (!timeout) {
					timeout = window.setTimeout(() => {
						setShowLocalVideo(true);
						timeout = 0;
					}, 500);
				}
			};
			if (localVideo.current) {
				localVideo.current.srcObject = videoCallsStore.localStream;
				localVideo.current.addEventListener('resize', cb);
			}

			return (): void => {
				if (timeout) {
					window.clearTimeout(timeout);
					timeout = 0;
				}
			};
		}, [videoCallsStore.localStream]);

		useEffect(() => {
			if (remoteVideo.current) {
				remoteVideo.current.srcObject = videoCallsStore.remoteStream;
			}
		}, [videoCallsStore.remoteStream]);

		const { formatMessage } = useIntl();

		return (
			<VideoPannelWrapper>
				<div
					ref={panel}
					className={`${fullscreen ? 'video-panel_fullscreen' : 'video-panel_normalscreen'} ${className}`}
				>
					<div className="control-buttons-container">
						<ControlButtons
							fullScreen={fullscreen}
							hideLocalVideo={(): void => setShowLocalVideo(false)}
							setFullScreen={setFullscreen}
							className="control_buttons"
						/>
					</div>
					<div className={`remote_video ${videoCallsStore.remoteVideoMuted ? 'remote_video_muted' : ''}`}>
						{videoCallsStore.remoteAudioMuted && <OperatorMuted />}
						{videoCallsStore.remoteStream ? (
							<>
								{videoCallsStore.remoteVideoMuted && (
									<span className="remote_video__text_camera-muted">
										{formatMessage(msgs.cameraMuted)}
									</span>
								)}
								<video
									playsInline
									autoPlay
									disablePictureInPicture
									controls={false}
									style={{ display: videoCallsStore.remoteVideoMuted ? 'none' : 'initial' }}
									ref={remoteVideo}
								/>
							</>
						) : (
							<LoadingOperatorLabel />
						)}
					</div>
					<div className="local_video">
						{videoCallsStore.videoMuted && (
							<div>
								<span>{formatMessage(msgs.cameraMuted)}</span>
							</div>
						)}
						<video
							className={videoCallsStore.currentCameraMode !== 'environment' ? 'video_reflected' : ''}
							autoPlay
							disablePictureInPicture
							controls={false}
							style={{ visibility: showLocalVideo ? 'visible' : 'hidden' }}
							playsInline
							ref={localVideo}
							muted
						/>
					</div>
				</div>
			</VideoPannelWrapper>
		);
	})
)(VideoPanelStyles);
